import type { PaginationMeta } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { BillingOrders, LicensePlan, PlanInput } from '@/modules/billing';

export const SAAS_ROUTES = {
  SUBSCRIBERS: '/saas/assinantes',
  SUBSCRIBER_DETAIL: '/saas/assinantes/:id',
  AUDIT: '/saas/auditoria',
  SETTINGS: '/saas/configuracoes',
  SETTINGS_ASSISTANT: '/saas/configuracoes/assistente',
  USERS: '/saas/usuarios',
  VENDA: '/saas/venda',
  GESTAO_FATURAMENTO: '/saas/gestao/faturamento',
  GESTAO_DFC: '/saas/gestao/dfc',
  GESTAO_DRE: '/saas/gestao/dre',
  GESTAO_RAIO_X: '/saas/gestao/raio-x',
  FINANCEIRO_CATEGORIAS: '/saas/financeiro/categorias',
  FINANCEIRO_CONTAS: '/saas/financeiro/contas',
  FINANCEIRO_A_PAGAR: '/saas/financeiro/contas-a-pagar',
  FINANCEIRO_A_RECEBER: '/saas/financeiro/contas-a-receber',
  subscriber: (id: string) => `/saas/assinantes/${id}`,
} as const;

const ENDPOINT = '/api/v1/saas';
const KEY = ['saas'];

export type SubscriberStatus = 'all' | 'active' | 'inactive' | 'waitlist';

export interface Subscriber {
  id: string;
  name: string;
  legalName: string | null;
  document: string | null;
  businessType: string;
  phone: string | null;
  planName: string;
  isActive: boolean;
  createdAt: string;
  unitsCount: number;
  usersCount: number;
  customersCount: number;
  creditBalance: number;
  whatsappUnits: number;
  onWaitlist: boolean;
}

export interface SubscribersQuery {
  search: string;
  status: SubscriberStatus;
  page: number;
  perPage: number;
}

export function useSubscribers(query: SubscribersQuery) {
  const params = new URLSearchParams({
    status: query.status,
    page: String(query.page),
    perPage: String(query.perPage),
  });

  if (query.search.trim()) params.set('search', query.search.trim());

  return useQuery({
    queryKey: [...KEY, 'subscribers', query],
    queryFn: () =>
      apiClient.get<{ data: Subscriber[]; meta: PaginationMeta }>(
        `${ENDPOINT}/subscribers?${params}`,
      ),
  });
}

export interface SubscribersOverview {
  total: number;
  active: number;
  inactive: number;
  withoutPlan: number;
  whatsappOnline: number;
  monthlyRevenue: number;
  creditBalance: number;
  waitlist: number;
}

export function useSubscribersOverview() {
  return useQuery({
    queryKey: [...KEY, 'overview'],
    queryFn: () => apiClient.get<SubscribersOverview>(`${ENDPOINT}/subscribers/overview`),
  });
}

export interface Plan {
  id: string;
  name: string;
  description: string | null;
  monthlyPrice: number;
  maxUnits: number | null;
  maxUsers: number | null;
  includedMessages: number | null;
  isActive: boolean;
}

export interface SubscriberUnit {
  id: string;
  name: string;
  city: string | null;
  whatsappFrom: string | null;
  whatsappStatus: string | null;
}

export interface SubscriberUser {
  id: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
}

export interface SubscriberDetail {
  subscriber: Subscriber;
  units: SubscriberUnit[];
  users: SubscriberUser[];
  plan: Plan | null;
  planStartedAt: string | null;
}

export interface SubscriberMetrics {
  services: number;
  professionals: number;
  customers: { active: number; inactive: number };
  campaignsThisMonth: number;
  messagesThisMonth: number;
  creditBalance: number;
  revenue: { month: string; total: number }[];
}

export type PendencyGroup = 'plano' | 'configuracao' | 'operacao';

export interface Pendency {
  key: string;
  group: PendencyGroup;
  title: string;
  hint: string;
  done: boolean;
}

export interface SubscriberPendencies {
  pending: number;
  total: number;
  items: Pendency[];
}

export function useSubscriber(id: string) {
  return useQuery({
    queryKey: [...KEY, 'subscriber', id],
    queryFn: () => apiClient.get<SubscriberDetail>(`${ENDPOINT}/subscribers/${id}`),
  });
}

export function useSubscriberMetrics(id: string) {
  return useQuery({
    queryKey: [...KEY, 'metrics', id],
    queryFn: () => apiClient.get<SubscriberMetrics>(`${ENDPOINT}/subscribers/${id}/metrics`),
  });
}

export function useSubscriberPendencies(id: string) {
  return useQuery({
    queryKey: [...KEY, 'pendencies', id],
    queryFn: () => apiClient.get<SubscriberPendencies>(`${ENDPOINT}/subscribers/${id}/pendencies`),
  });
}

export interface AuditEntry {
  id: string;
  companyId: string | null;
  companyName: string | null;
  userId: string | null;
  userName: string;
  method: string;
  route: string;
  path: string;
  entity: string;
  entityId: string | null;
  statusCode: number;
  createdAt: string;
}

export interface AuditQuery {
  search: string;
  companyId: string;
  entity: string;
  from: string;
  to: string;
  page: number;
  perPage: number;
}

export function useAudit(query: AuditQuery) {
  const params = new URLSearchParams({
    page: String(query.page),
    perPage: String(query.perPage),
  });

  for (const campo of ['search', 'companyId', 'entity', 'from', 'to'] as const) {
    const valor = query[campo].trim();
    if (valor) params.set(campo, valor);
  }

  return useQuery({
    queryKey: [...KEY, 'audit', query],
    queryFn: () =>
      apiClient.get<{ data: AuditEntry[]; meta: PaginationMeta }>(`${ENDPOINT}/audit?${params}`),
  });
}

export type MessagePricingMode = 'AUTO' | 'FIXED';

export interface TwilioMockScope {
  key: string;
  label: string;
  mocked: boolean;
  covers: string[];
}

export interface TwilioMock {
  url: string | null;
  hosts: { host: string; domain: string; mocked: boolean }[];
  scopes: TwilioMockScope[];
  neverMocked: { key: string; label: string; covers: string[] };
}

export interface SaasSettings {
  messagePricingMode: MessagePricingMode;
  messagePrice: number;
  messageMarginPercent: number;
  usdRate: number;
  lineMarginPercent: number;
  minCreditTopUp: number;
  asaasEnvironment: AsaasEnvironment;
  asaasReturnUrl: string | null;
  asaasApiKeySet: boolean;
  asaasApiKeyHint: string | null;
  asaasApiKeyFromEnv: boolean;
  asaasWebhookTokenSet: boolean;
  asaasWebhookUrl: string;
  /** Ligada: quem termina o cadastro entra na fila de espera. */
  waitlistEnabled: boolean;
  adminPollingMinutes: number;
  providerPriceUsd: number | null;
  effectivePrice: number | null;
  twilioMock: TwilioMock;
  updatedAt: string;
}

export type AsaasEnvironment = 'SANDBOX' | 'PRODUCTION';

export type SaasSettingsInput = Pick<
  SaasSettings,
  | 'messagePricingMode'
  | 'messagePrice'
  | 'messageMarginPercent'
  | 'usdRate'
  | 'lineMarginPercent'
  | 'minCreditTopUp'
  | 'asaasEnvironment'
  | 'asaasReturnUrl'
  | 'waitlistEnabled'
  | 'adminPollingMinutes'
> & {
  /** Ausente mantem o segredo gravado; string vazia apaga. */
  asaasApiKey?: string;
  asaasWebhookToken?: string;
};

export function useSaasSettings() {
  return useQuery({
    queryKey: [...KEY, 'settings'],
    queryFn: () => apiClient.get<SaasSettings>(`${ENDPOINT}/settings`),
  });
}

export function useSaveSaasSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SaasSettingsInput) =>
      apiClient.put<SaasSettings>(`${ENDPOINT}/settings`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Todos os planos, ativos ou nao - a tabela inteira, pra quem administra. */
export function useSaasPlans() {
  return useQuery({
    queryKey: [...KEY, 'plans'],
    queryFn: () => apiClient.get<{ data: LicensePlan[] }>(`${ENDPOINT}/plans`),
  });
}

export type BusinessXrayCategory =
  | 'ADIMPLENTES'
  | 'INADIMPLENTES'
  | 'LEADS_NOVOS'
  | 'LEADS_CONFIGURANDO'
  | 'LEADS_COMPLETOS';

export interface BusinessXrayReport {
  months: number;
  total: number;
  estimatedRevenue: number;
  normalRevenue: number;
  promotionalSavings: number;
  categories: {
    key: BusinessXrayCategory;
    label: string;
    count: number;
    estimatedRevenue: number;
  }[];
  plans: {
    id: string;
    name: string;
    count: number;
    estimatedRevenue: number;
    normalRevenue: number;
  }[];
}

export function useBusinessXray() {
  return useMutation({
    mutationFn: (input: {
      categories: BusinessXrayCategory[];
      planIds: string[];
      months: number;
    }) => apiClient.post<BusinessXrayReport>(`${ENDPOINT}/business-xray`, input),
  });
}

function usePlansMutation<TArgs>(mutationFn: (args: TArgs) => Promise<LicensePlan>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...KEY, 'plans'] });
      queryClient.invalidateQueries({ queryKey: ['public-plans'] });
    },
  });
}

export function useCreatePlan() {
  return usePlansMutation((input: PlanInput) =>
    apiClient.post<LicensePlan>(`${ENDPOINT}/plans`, input),
  );
}

export function useUpdatePlan() {
  return usePlansMutation(({ id, input }: { id: string; input: PlanInput }) =>
    apiClient.patch<LicensePlan>(`${ENDPOINT}/plans/${id}`, input),
  );
}

export function useSubscriberPayments(id: string, page: number) {
  return useQuery({
    queryKey: [...KEY, id, 'payments', page],
    enabled: Boolean(id),
    queryFn: () =>
      apiClient.get<BillingOrders>(
        `${ENDPOINT}/subscribers/${id}/payments?page=${page}&perPage=20`,
      ),
  });
}

export interface SaasUser {
  id: string;
  name: string;
  email: string;
  image: string | null;
  isActive: boolean;
  createdAt: string;
}

export function useSaasUsers() {
  return useQuery({
    queryKey: [...KEY, 'users'],
    queryFn: () => apiClient.get<{ data: SaasUser[] }>(`${ENDPOINT}/users`),
  });
}

export function useCreateSaasUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { name: string; email: string; password: string }) =>
      apiClient.post<SaasUser>(`${ENDPOINT}/users`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateSaasUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; name?: string; isActive?: boolean }) =>
      apiClient.patch<SaasUser>(`${ENDPOINT}/users/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
